import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import firebase from 'firebase/compat/app';
import ConfirmDialog from 'components/common/ConfirmDialog';
import { subscribePendingQueueItems } from '../services/drugReferenceService';
import { QUEUE_STATUS } from '../constants/collections';
import { CONFIDENCE_THRESHOLD } from '../config/confidenceThreshold';

const reviewDecision = firebase.functions().httpsCallable('drugReferenceReviewDecision');

export default function DrugReviewQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actingId, setActingId] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);

  useEffect(() => {
    const unsub = subscribePendingQueueItems(
      (rows) => {
        setItems(rows);
        setLoading(false);
      },
      (err) => {
        setError(err.message || 'Failed to load review queue.');
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const handleDecision = async (queueId, decision) => {
    setActingId(queueId);
    setError('');
    setSuccess('');
    try {
      await reviewDecision({ queueId, decision });
      setSuccess(`Item ${decision}.`);
    } catch (err) {
      setError(err.message || `Failed to ${decision} item.`);
    } finally {
      setActingId(null);
      setRejectTarget(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
      <Button
        component={RouterLink}
        to="/dashboard/drug-reference"
        startIcon={<ArrowBackRounded />}
        sx={{ mb: 2 }}
      >
        Back to Drug Reference
      </Button>

      <Typography variant="h5" gutterBottom>
        Drug Reference — Review Queue
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Items below threshold ({CONFIDENCE_THRESHOLD}) or flagged for clinical confirmation.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {items.length === 0 ? (
        <Typography color="text.secondary">No pending review items.</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Drug</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Diff summary</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Stack spacing={0.5}>
                      <Typography variant="body2">{item.drugId}</Typography>
                      <Button
                        component={RouterLink}
                        to={`/dashboard/drug-reference/drug/${item.drugId}`}
                        size="small"
                      >
                        Open card
                      </Button>
                    </Stack>
                  </TableCell>
                  <TableCell>{item.country || 'US'}</TableCell>
                  <TableCell>{item.reviewReason || item.changeType}</TableCell>
                  <TableCell>
                    {item.confidenceScore != null ? item.confidenceScore.toFixed(2) : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {item.diffSummary || Object.keys(item.proposedData || {}).join(', ') || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <LoadingButton
                        size="small"
                        variant="contained"
                        loading={actingId === item.id}
                        onClick={() => handleDecision(item.id, QUEUE_STATUS.APPROVED)}
                      >
                        Approve
                      </LoadingButton>
                      <Button size="small" onClick={() => setRejectTarget(item)}>
                        Reject
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmDialog
        open={Boolean(rejectTarget)}
        title="Reject queued change?"
        message="This will mark the queue item rejected without publishing the proposed data."
        confirmColor="error"
        loading={Boolean(actingId)}
        onConfirm={() => handleDecision(rejectTarget?.id, QUEUE_STATUS.REJECTED)}
        onCancel={() => setRejectTarget(null)}
      />
    </Box>
  );
}
